import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface CustomExercise {
  id: string;
  name: string;
  description?: string;
  category: string;
  muscle_groups: string[];
  equipment?: string;
  instructions?: string;
}

interface AddCustomExerciseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExerciseAdded: (exercise: CustomExercise) => void;
}

const CATEGORIES = [
  "Chest", "Back", "Shoulders", "Arms", "Legs", "Core", "Cardio", "Full Body", "Other"
];

const MUSCLE_GROUPS = [
  "Chest", "Back", "Shoulders", "Biceps", "Triceps", "Forearms", 
  "Quadriceps", "Hamstrings", "Glutes", "Calves", "Abs", "Obliques", 
  "Traps", "Lats", "Delts", "Hip Flexors"
];

const EQUIPMENT_OPTIONS = [
  "Barbell", "Dumbbell", "Kettlebell", "Cable", "Machine", "Bodyweight", 
  "Resistance Band", "Medicine Ball", "TRX", "Plate", "Other", "None"
];

export const AddCustomExerciseModal = ({ isOpen, onClose, onExerciseAdded }: AddCustomExerciseModalProps) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "",
    equipment: "",
    instructions: ""
  });
  const [selectedMuscleGroups, setSelectedMuscleGroups] = useState<string[]>([]);
  const [muscleGroupInput, setMuscleGroupInput] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast.error("You must be logged in to create custom exercises");
      return;
    }

    if (!formData.name.trim()) {
      toast.error("Exercise name is required");
      return;
    }

    if (!formData.category) {
      toast.error("Please select a category");
      return;
    }

    if (selectedMuscleGroups.length === 0) {
      toast.error("Please add at least one muscle group");
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error } = await supabase
        .from('custom_exercises')
        .insert({
          user_id: user.id,
          name: formData.name.trim(),
          description: formData.description.trim() || null,
          category: formData.category,
          muscle_groups: selectedMuscleGroups,
          equipment: formData.equipment || null,
          instructions: formData.instructions.trim() || null
        })
        .select()
        .single();

      if (error) throw error;

      const newExercise: CustomExercise = {
        id: data.id,
        name: data.name,
        description: data.description,
        category: data.category,
        muscle_groups: data.muscle_groups,
        equipment: data.equipment,
        instructions: data.instructions
      };

      onExerciseAdded(newExercise);
      toast.success("Custom exercise created successfully!");
      
      // Reset form
      setFormData({ name: "", description: "", category: "", equipment: "", instructions: "" });
      setSelectedMuscleGroups([]);
      setMuscleGroupInput("");
      onClose();

    } catch (error) {
      console.error('Error creating custom exercise:', error);
      toast.error("Failed to create custom exercise");
    } finally {
      setIsSubmitting(false);
    }
  };

  const addMuscleGroup = () => {
    const muscle = muscleGroupInput.trim();
    if (muscle && !selectedMuscleGroups.includes(muscle)) {
      setSelectedMuscleGroups([...selectedMuscleGroups, muscle]);
      setMuscleGroupInput("");
    }
  };

  const removeMuscleGroup = (muscle: string) => {
    setSelectedMuscleGroups(selectedMuscleGroups.filter(m => m !== muscle));
  };

  const handleMuscleGroupKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addMuscleGroup();
    }
  };

  const addPredefinedMuscleGroup = (muscle: string) => {
    if (!selectedMuscleGroups.includes(muscle)) {
      setSelectedMuscleGroups([...selectedMuscleGroups, muscle]);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Custom Exercise</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Exercise Name */}
          <div>
            <Label htmlFor="exercise-name">Exercise Name *</Label>
            <Input
              id="exercise-name"
              placeholder="e.g., Incline Cable Fly"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="mt-2"
            />
          </div>

          {/* Description */}
          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Brief description of the exercise..."
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="mt-2"
              rows={3}
            />
          </div>

          {/* Category */}
          <div>
            <Label>Category *</Label>
            <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
              <SelectTrigger className="mt-2">
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((category) => (
                  <SelectItem key={category} value={category}>
                    {category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Muscle Groups */}
          <div>
            <Label>Muscle Groups *</Label>
            <div className="mt-2 space-y-3">
              {/* Quick add buttons */}
              <div className="flex flex-wrap gap-2">
                {MUSCLE_GROUPS.map((muscle) => (
                  <Button
                    key={muscle}
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addPredefinedMuscleGroup(muscle)}
                    disabled={selectedMuscleGroups.includes(muscle)}
                    className="h-7 text-xs"
                  >
                    {muscle}
                  </Button>
                ))}
              </div>
              
              {/* Custom muscle group input */}
              <div className="flex gap-2">
                <Input
                  placeholder="Or type custom muscle group..."
                  value={muscleGroupInput}
                  onChange={(e) => setMuscleGroupInput(e.target.value)}
                  onKeyDown={handleMuscleGroupKeyPress}
                  className="flex-1"
                />
                <Button type="button" onClick={addMuscleGroup} size="sm">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              
              {/* Selected muscle groups */}
              {selectedMuscleGroups.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {selectedMuscleGroups.map((muscle) => (
                    <Badge key={muscle} variant="secondary" className="flex items-center gap-1">
                      {muscle}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeMuscleGroup(muscle)}
                        className="h-auto p-0 hover:bg-transparent"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Equipment */}
          <div>
            <Label>Equipment</Label>
            <Select value={formData.equipment} onValueChange={(value) => setFormData({ ...formData, equipment: value })}>
              <SelectTrigger className="mt-2">
                <SelectValue placeholder="Select equipment" />
              </SelectTrigger>
              <SelectContent>
                {EQUIPMENT_OPTIONS.map((equipment) => (
                  <SelectItem key={equipment} value={equipment}>
                    {equipment}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Instructions */}
          <div>
            <Label htmlFor="instructions">Instructions</Label>
            <Textarea
              id="instructions"
              placeholder="How to perform this exercise..."
              value={formData.instructions}
              onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
              className="mt-2"
              rows={4}
            />
          </div>
        </form>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            onClick={handleSubmit} 
            disabled={isSubmitting}
            className="bg-gradient-primary text-black"
          >
            {isSubmitting ? "Creating..." : "Create Exercise"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};